import React from "react";
import { useNavigate } from "react-router-dom";
import GenericModal from "@ui-components/Modal/GenericModal";

export interface RecordFact {
  label: string;
  /** Empty or "–" hides the fact: a label with no value reads as missing data. */
  value: string | null | undefined;
}

/** A fact worth a line: absent values and the API's "–" placeholder are not. */
function hasValue(fact: RecordFact): fact is RecordFact & { value: string } {
  return Boolean(fact.value) && fact.value !== "–";
}

/**
 * The frame of every outage and maintenance record opened from Kontrol Edge:
 * the facts in one line, the full text below, and the way to ask for more.
 * One frame so a record reads the same from the Overview and from its list.
 */
export function RecordDetailsModal({
  isOpen,
  title,
  facts,
  paragraphs,
  onClose,
}: {
  isOpen: boolean;
  title: string;
  facts: RecordFact[];
  /** Shown in order; blanks and repeats are dropped (a summary often equals its detail). */
  paragraphs: Array<string | null | undefined>;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const text = Array.from(
    new Set(paragraphs.map((paragraph) => paragraph?.trim()).filter(Boolean))
  ) as string[];

  return (
    <GenericModal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-semantic-text-secondary">
          {facts.filter(hasValue).map((fact) => (
            <span key={fact.label}>
              <strong>{fact.label}:</strong> {fact.value}
            </span>
          ))}
        </div>

        {text.map((paragraph) => (
          <p
            key={paragraph}
            className="whitespace-pre-wrap text-sm text-semantic-text-secondary"
          >
            {paragraph}
          </p>
        ))}

        <div className="border-t border-semantic-border-primary pt-3">
          <p className="text-xs text-semantic-text-secondary">
            For more information, open a{" "}
            <a
              href="/support/request"
              className="underline hover:text-semantic-text-primary"
              onClick={(event) => {
                event.preventDefault();
                onClose();
                navigate("/support/request");
              }}
            >
              help request
            </a>
            .
          </p>
        </div>
      </div>
    </GenericModal>
  );
}
